import { Property } from '@components/DataDisplay/Property';
import { PropertyGroup } from '@components/DataDisplay/PropertyGroup';
import { TextProperty } from '@components/DataDisplay/TextProperty';
import { DownloadLinkButton } from '@components/Download/DownloadLinkButton';
import { UserAvatar } from '@components/UserAvatar/UserAvatar';
import { SignatureDialog } from '@modules/documents/components/DocumentSignatureDialog';
import { RequestSignatureDialog } from '@modules/documents/components/RequestSignatureDialog/RequestSignatureDialog';
import { ElectronicSignatureStatusColor, ElectronicSignatureStatusMessages } from '@modules/documents/types';
import { useProject } from '@modules/projects/utils/useProject';
import { Chip, Divider, Stack, Typography } from '@mui/material';
import { getTenantIdentifier } from '@utils/getTenantIdentifier';
import dayjs from 'dayjs';
import { DocumentFragmentFragment, SignatureStatus } from 'gql/index';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

interface OwnProps {
  document?: DocumentFragmentFragment | null;
}

export const DocumentSignatureDetails: React.FC<OwnProps> = ({ document }) => {
  const { formatMessage } = useIntl();
  const tenantIdentifier = getTenantIdentifier();
  const { projectId, isCurrentProjectAdmin } = useProject({ projectId: document?.projectId });

  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [openAskSignatureDialog, setOpenAskSignatureDialog] = useState(false);

  const signatureStatus = useMemo(() => {
    if (document?.signatureRequest?.signatureStatus)
      return document?.signatureRequest?.signatureStatus;

    if (document?.requiresSignatures) {
      return document?.isSigned ? SignatureStatus.Signed : SignatureStatus.Pending;
    }

    return undefined;
  }, [document?.signatureRequest?.signatureStatus, document?.isSigned, document?.requiresSignatures]);

  return (
    <>
      <PropertyGroup
        title={formatMessage({ id: 'Signature' })}
      >
        <TextProperty propertyName={formatMessage({ id: 'Require signature' })} propertyValue={document?.requiresSignatures ? formatMessage({ id: 'Yes' }) : formatMessage({ id: 'No' })} />

        {document?.requiresSignatures &&
          <Stack gap={2}>
            <Property propertyName={formatMessage({ id: 'Require signature from' })}>
              <Stack direction='row'>
                <Chip
                  avatar={<UserAvatar displayName={document?.signatoryCustomer?.fullName} />}
                  label={document?.signatoryCustomer?.fullName}
                />
              </Stack>
            </Property>

            <TextProperty
              propertyName={formatMessage({ id: 'Requested on' })}
              propertyValue={dayjs(document?.signatureTask?.startDate).format('LLL')}
            />

            <TextProperty
              propertyName={formatMessage({ id: 'Due date' })}
              propertyValue={dayjs(document.signatureRequest?.expiresOn ?? document?.signatureTask?.dueDate).format('LLL')}
            />

            {signatureStatus && <Property
              propertyName={formatMessage({ id: 'Status' })}
            >
              <Stack alignItems={'center'} direction='row' gap={1}>
                <Chip
                  size='small'
                  color={ElectronicSignatureStatusColor[signatureStatus]}
                  label={formatMessage(ElectronicSignatureStatusMessages[signatureStatus])} />
              </Stack>
            </Property>
            }

            {document.isSigned &&
              <TextProperty
                propertyName={formatMessage({ id: 'Signed on' })}
                propertyValue={document?.isSigned ? dayjs(document.signedOn).format('LLL') : ''}
              />}
          </Stack>
        }

        {(document?.isSigned && document.zohoRequestId && isCurrentProjectAdmin) && (
          <DownloadLinkButton
            variant='Text'
            buttonText={formatMessage({ id: 'Download signing certificate' })}
            fileName={formatMessage({ id: 'Signing certificate' })}
            link={`/${tenantIdentifier}/api/projects/${projectId}/documents/${document.id}/signing-certificate`} />
        )}

        {(document?.isSigned && !document.zohoRequestId) &&
          <Property propertyName={formatMessage({ id: 'Signature' })}>
            <Stack>
              {document.textualSignature ? (
                <>
                  <Typography fontFamily='Dancing Script' fontSize='1.5rem'>{document.textualSignature}</Typography>
                  <Divider />
                </>
              ) : (
                <img
                  src={`/${getTenantIdentifier()}/api/documents/${document.id}/signature`}
                  style={{ backgroundColor: 'white' }}
                />
              )}
            </Stack>
          </Property>
        }
      </PropertyGroup>

      {document?.id && document?.folderEntry?.folderId && document.requiresSignatures && document?.projectId && (
        <RequestSignatureDialog
          documentId={document?.id}
          projectId={document?.projectId}
          open={openAskSignatureDialog}
          onClose={() => setOpenAskSignatureDialog(false)}
          />
      )}

{ document?.id && document?.projectId && (
      <SignatureDialog
        documentId={document?.id ?? 0}
        projectId={document?.projectId}
        actionId={document?.zohoActionId ?? ''}
        requestId={document?.zohoRequestId ?? ''}
        open={isSignatureDialogOpen}
        onClose={() => setIsSignatureDialogOpen(false)}
      />
)}
    </>
  );
};